import cn from "classnames";
import { featureModuleTypesEnum } from "helpers/enums";
import { isRegionDryingPeriodsEnabledForClients } from "helpers/regionDryingPeriods";
import { canManageOrganization } from "helpers/system";
import React, { useMemo, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Popover } from "react-tiny-popover";

const BidDataFilter = ({
    filters,
    toggleFilter,
    toggleGroupFilter,
    availableFeatureModules,
    inEditRoute,
    roadRestrictionTypes,
    roles,
}) => {
    const [opened, setOpened] = useState(false);
    const groupItems = useMemo(() => {
        const items = {
            allCoordinates: ["mobile", "tracker", "stops"],
            roadObjects: ["refuelings", "tollRoads", "platonPoints", "paymentPoints", "emergencyAreaPoints"],
            roadRestrictions: roadRestrictionTypes.map((item) => item.id),
        };
        if (isRegionDryingPeriodsEnabledForClients) {
            items.roadObjects.push("regionDryingFilters");
        }
        if (availableFeatureModules.includes(featureModuleTypesEnum.DocumentAssignment)) {
            items.roadObjects.push("postamats");
        }
        if (availableFeatureModules.includes(featureModuleTypesEnum.UnstableZones) && canManageOrganization(roles)) {
            items.roadObjects.push("unstableZones");
        }
        if (availableFeatureModules.includes(featureModuleTypesEnum.RouteControlPoints)) {
            items.roadObjects.push("routeControlPoints");
        }
        return items;
    }, [availableFeatureModules, roadRestrictionTypes]);

    const btnClasses = (name) => cn("checkbox d-inline-block", { "checkbox--checked": filters[name] ?? true });
    const btnGroupClasses = (name) => {
        const { allChecked, someChecked } = groupItems[name].reduce(
            (acc, item) => {
                acc.allChecked = acc.allChecked && (filters[item] ?? true);
                acc.someChecked = acc.someChecked || (filters[item] ?? true);
                return acc;
            },
            { allChecked: true, someChecked: false },
        );
        return cn("checkbox d-inline-block", {
            "checkbox--checked": allChecked,
            "checkbox--indeterminate": someChecked && !allChecked,
        });
    };

    const toggleGroupFilterHandle = (name) => {
        const someUnchecked = groupItems[name].some((item) => !(filters[item] ?? true));
        const newItems = groupItems[name].reduce((acc, item) => ({ ...acc, [item]: someUnchecked }), {});
        toggleGroupFilter(newItems);
    };

    const popoverContent = (
        <div className="map-bid-filter__list">
            <Scrollbars autoHeight autoHeightMin={200} autoHeightMax={500}>
                {!inEditRoute && (
                    <>
                        <div
                            className="map-bid-filter__list-item map-bid-filter__list-item--first"
                            onClick={toggleGroupFilterHandle.bind(this, "allCoordinates")}
                        >
                            <div className={btnGroupClasses("allCoordinates")} />
                            &nbsp;
                            <div className="pl-1 d-inline-block checkbox__text">Все координаты</div>
                        </div>
                        <div className="pb-2">
                            <div className="pl-4 map-bid-filter__list-item" onClick={toggleFilter.bind(this, "mobile")}>
                                <div className={btnClasses("mobile")} />
                                &nbsp;
                                <div className="pl-1 d-inline-block checkbox__text">Мобильные координаты</div>
                            </div>
                            <div
                                className="pl-4 map-bid-filter__list-item"
                                onClick={toggleFilter.bind(this, "tracker")}
                            >
                                <div className={btnClasses("tracker")} />
                                &nbsp;
                                <div className="pl-1 d-inline-block checkbox__text">Координаты трекера</div>
                            </div>
                            <div className="pl-4 map-bid-filter__list-item" onClick={toggleFilter.bind(this, "stops")}>
                                <div className={btnClasses("stops")} />
                                &nbsp;
                                <div className="pl-1 d-inline-block checkbox__text">Точки остановок</div>
                            </div>
                        </div>
                    </>
                )}
                <div
                    className="map-bid-filter__list-item map-bid-filter__list-item--first"
                    onClick={toggleGroupFilterHandle.bind(this, "roadObjects")}
                >
                    <div className={btnGroupClasses("roadObjects")} />
                    &nbsp;
                    <div className="pl-1 d-inline-block checkbox__text">Объекты дорог</div>
                </div>
                <div className="pb-2">
                    {!inEditRoute && (
                        <div className="pl-4 map-bid-filter__list-item" onClick={toggleFilter.bind(this, "refuelings")}>
                            <div className={btnClasses("refuelings")} />
                            &nbsp;
                            <div className="pl-1 d-inline-block checkbox__text">Заправки</div>
                        </div>
                    )}
                    <div className="pl-4 map-bid-filter__list-item" onClick={toggleFilter.bind(this, "tollRoads")}>
                        <div className={btnClasses("tollRoads")} />
                        &nbsp;
                        <div className="pl-1 d-inline-block checkbox__text">Платные дороги</div>
                    </div>
                    {availableFeatureModules.includes(featureModuleTypesEnum.DocumentAssignment) && (
                        <div className="pl-4 map-bid-filter__list-item" onClick={toggleFilter.bind(this, "postamats")}>
                            <div className={btnClasses("postamats")} />
                            &nbsp;
                            <div className="pl-1 d-inline-block checkbox__text">Постаматы</div>
                        </div>
                    )}
                    <div className="pl-4 map-bid-filter__list-item" onClick={toggleFilter.bind(this, "platonPoints")}>
                        <div className={btnClasses("platonPoints")} />
                        &nbsp;
                        <div className="pl-1 d-inline-block checkbox__text">Рамки Платон</div>
                    </div>
                    <div className="pl-4 map-bid-filter__list-item" onClick={toggleFilter.bind(this, "paymentPoints")}>
                        <div className={btnClasses("paymentPoints")} />
                        &nbsp;
                        <div className="pl-1 d-inline-block checkbox__text">Пункты оплаты</div>
                    </div>
                    <div
                        className="pl-4 map-bid-filter__list-item"
                        onClick={toggleFilter.bind(this, "emergencyAreaPoints")}
                    >
                        <div className={btnClasses("emergencyAreaPoints")} />
                        &nbsp;
                        <div className="pl-1 d-inline-block checkbox__text">Аварийные участки</div>
                    </div>
                    {availableFeatureModules.includes(featureModuleTypesEnum.UnstableZones) && (
                        <div
                            className="pl-4 map-bid-filter__list-item"
                            onClick={toggleFilter.bind(this, "unstableZones")}
                        >
                            <div className={btnClasses("unstableZones")} />
                            &nbsp;
                            <div className="pl-1 d-inline-block checkbox__text">Зоны нестабильных координат</div>
                        </div>
                    )}
                    {availableFeatureModules.includes(featureModuleTypesEnum.RouteControlPoints) && (
                        <div
                            className="pl-4 map-bid-filter__list-item"
                            onClick={toggleFilter.bind(this, "routeControlPoints")}
                        >
                            <div className={btnClasses("routeControlPoints")} />
                            &nbsp;
                            <div className="pl-1 d-inline-block checkbox__text">Точки контроля маршрута</div>
                        </div>
                    )}
                    {isRegionDryingPeriodsEnabledForClients && (
                        <div
                            className="pl-4 map-bid-filter__list-item"
                            onClick={toggleFilter.bind(this, "regionDryingFilters")}
                        >
                            <div className={btnClasses("regionDryingFilters")} />
                            &nbsp;
                            <div className="pl-1 d-inline-block checkbox__text">Зоны просушек</div>
                        </div>
                    )}
                </div>
                {!!roadRestrictionTypes?.length && (
                    <>
                        <div
                            className="map-bid-filter__list-item map-bid-filter__list-item--first"
                            onClick={toggleGroupFilterHandle.bind(this, "roadRestrictions")}
                        >
                            <div className={btnGroupClasses("roadRestrictions")} />
                            &nbsp;
                            <div className="pl-1 d-inline-block checkbox__text">Объекты ограничений</div>
                        </div>
                        <div className="pb-2">
                            {roadRestrictionTypes.map((item) => (
                                <div
                                    key={item.id}
                                    className="pl-4 map-bid-filter__list-item"
                                    onClick={toggleFilter.bind(this, item.id)}
                                >
                                    <div className={btnClasses(item.id)} />
                                    &nbsp;
                                    <div className="pl-1 d-inline-block checkbox__text">{item.name}</div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </Scrollbars>
        </div>
    );

    return (
        <div className="map-bid-filter">
            <Popover isOpen={opened} positions="left" align="start" content={popoverContent}>
                <div
                    className="map-bid-filter__button"
                    data-onboarding="bid-map-filter-button"
                    onClick={() => setOpened(!opened)}
                />
            </Popover>
        </div>
    );
};

export default BidDataFilter;
